import { ApolloError, useMutation } from '@apollo/client';
import { ConfirmUserAccountInput } from '__generated__/globalTypes';
import gql from 'graphql-tag';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { tracking } from 'App/Tracking';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { LoginAction } from 'App/Auth';
import {
  confirmUserAccount as ConfirmUserAccountResponse,
  confirmUserAccount_confirmUserAccount as ConfirmUserAccountData,
} from './__generated__/confirmUserAccount';

export const CONFIRM_USER_ACCOUNT_MUTATION = gql`
  mutation confirmUserAccount($input: ConfirmUserAccountInput!) {
    confirmUserAccount(input: $input) {
      code
      success
      token
    }
  }
`;

enum ConfirmUserAccountError {
  OrganisationInactive = 'OrganisationInactiveError',
}

interface UseConfirmUserAccountProps {
  onSuccess(data: ConfirmUserAccountData): void;
}

type ConfirmUserAccountPayload = Omit<ConfirmUserAccountInput, 'isMobile'>;

interface UseConfirmUserAccountResult {
  confirmUserAccount(input: ConfirmUserAccountPayload): Promise<void>;
  error: string | undefined;
  loading: boolean;
}

export const useConfirmUserAccount = ({
  onSuccess,
}: UseConfirmUserAccountProps): UseConfirmUserAccountResult => {
  const { t: translate } = useTranslation('logged_out', {
    keyPrefix: 'sign_up',
  });

  const [error, setError] = useState<string | undefined>();
  const DEFAULT_ERROR_MESSAGE = translate('errors.default');

  const handleError = (e: ApolloError): void => {
    const subdomain = getSubdomainFromUrl();
    tracking.track('login-failed', {
      subdomain,
      loginAction: LoginAction.REGISTRATION,
    });
    if (e.graphQLErrors.length > 0) {
      const errorName = e.graphQLErrors[0].name;
      if (errorName === ConfirmUserAccountError.OrganisationInactive) {
        setError(translate('errors.organisation_inactive'));
      } else {
        setError(DEFAULT_ERROR_MESSAGE);
      }
    } else {
      setError(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleOnComplete = (data: ConfirmUserAccountResponse): void => {
    if (data.confirmUserAccount?.success) {
      setError(undefined);
      onSuccess(data.confirmUserAccount);
    } else {
      setError(DEFAULT_ERROR_MESSAGE);
    }
  };

  const [mutation, { loading }] = useMutation(CONFIRM_USER_ACCOUNT_MUTATION, {
    onCompleted: handleOnComplete,
    onError: handleError,
  });

  const confirmUserAccount = async (input: ConfirmUserAccountPayload) => {
    await mutation({ variables: { input: { ...input, isMobile: false } } });
  };

  return { confirmUserAccount, error, loading };
};
